import { BlockError } from '@/blocks.04-project/block-error/block-error';
import toArray from 'lodash-es/toArray';
import forEach from 'lodash-es/forEach';
import every from 'lodash-es/every';
import merge from 'lodash-es/merge';
import get from 'lodash-es/get';

/***
 * Класс для валидации форм
 *
 * @example
 * Аттрибуты:
 * data-iv-validation {boolean, default = false} - вкл/вкл кастомную валидацию
 * novalidate {boolean, default = false} - вкл/вкл стандартную валидацию
 *
 * Аттрибуты на input
 * data-empty-text {string, default = 'Заполните поле'} - текст ошибки при пустом значении
 * data-too-short {string, default = 'Минимальное количество символов '} - текст ошибки при недостаточном размере текста
 * data-too-long {string, default = 'Заполните поле'} - текст ошибки при превышении размере текста
 * data-pattern-mismatch {string, default = 'Не правильный формат ввода'} - текст ошибки при не соответствии с паттерном
 * data-validation-important {boolean, default = false} - input отмеченный этим аттрибутом, блокирует кнопку до тех пор, пока не будет валиден
 * data-confirm {boolean, default = false} - input отмеченный этим аттрибутом, будет проверяться на совпадение с другим
 * data-confirm-target {string, default = ''} - селектор связанного инпута для проверки
 *
 * Аттрибуты на кнопке submit
 * data-validation-button {boolean, default = false} - отмечает кнопки, которые нужно отключать при невалидной форме
 *
 * Классы формы:
 * valid - вешается на форму после валидации если в ней нет не валидных элементов
 * invalid - вешается на форму после валидации если в ней есть не валидные элементы
 * was-validated - вешается на форму, которая была провалидирована
 *
 * Классы элементов:
 * invalid - вешается на невалидный инпут после проверки
 * invalid-feedback - элемент с сообщение об ошибке
 *
 * Переменные
 * isValidForm - хранит в себе валидность формы
 */
class Validation {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с аттрибутом data-validation
   */
  constructor(elem) {
    this.BlockError = new BlockError({ blockName: 'Validation', destroy: this.destroy });

    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.Validation = this;
    this.DICTIONARY = {
      email: window.BX?.message('validationInvalidEmail') || 'Введите корректный email',
      tel: window.BX?.message('validationInvalidTel') || 'Введите корректный номер телефона',
      empty: window.BX?.message('validationInvalidEmpty') || 'Заполните поле',
      patternMismatch: window.BX?.message('validationPatternMismatch') || 'Неправильный формат ввода',
      tooShort: window.BX?.message('validationInvalidTooShort') || 'Минимальное количество символов ',
      tooLong: window.BX?.message('validationInvalidTooLong') || 'Максимальное количество символов ',
      notEqual: window.BX?.message('validationInvalidNotEqual') || 'Пароли должны совпадать',
      emptyRating: window.BX?.message('validationInvalidEmptyRating') || 'Оцените товар',
    };

    this.init();
  }

  init = () => {
    this.elements = toArray(this.block.querySelectorAll('input:not([type="hidden"]), textarea, select'));
    this.importantElements = toArray(this.block.querySelectorAll('[data-validation-important]'));
    this.submitButtons = toArray(this.block.querySelectorAll('[data-validation-button]'));
    this.isValidForm = true;

    if (this.importantElements.length) {
      this.checkImportantValidity();
      this.block.addEventListener('input', this.checkImportantValidity);
      this.block.addEventListener('change', this.checkImportantValidity);
    }

    this.block.addEventListener('submit', this.checkValidity, { capture: true }); // capture чтобы захватывать событие раньше остальных (inputmask)
    this.block.addEventListener('reset', this.resetValidity);
    this.block.classList.add('validation_inited');
  };

  /***
   * Проверка валидации формы на submit
   * @param evt {Event} - событие submit
   */
  checkValidity = (evt) => {
    this.elements = toArray(this.block.querySelectorAll('input:not([type="hidden"]), textarea, select'));
    let isValidForm = true;
    if (!this.block.classList.contains('was-validated')) {
      this.block.addEventListener('input', this.checkFormValidity);
      this.block.addEventListener('change', this.checkFormValidity);

      if (this.importantElements.length) {
        this.block.removeEventListener('input', this.checkImportantValidity);
        this.block.removeEventListener('change', this.checkImportantValidity);
      }
    }

    forEach(this.elements, (elem) => {
      if (elem.disabled || elem.readOnly) return;
      const isValidInput = this.checkInputValidity(elem);
      if (isValidForm  && !isValidInput) {
        isValidForm = false;
        this.showInvalid(evt);
        elem.focus();
      }
    });

    this.block.classList.toggle('invalid', !isValidForm);
    this.block.classList.toggle('valid', isValidForm);
    this.isValidForm = isValidForm;
    this.toggleSubmitButton(!isValidForm);
  };

  /***
   * Проверка input на валидацию
   * @param elem {HTMLInputElement} - элемент для проверки
   * @return {boolean} - true - input валидный; false: input не валидный
   */
  checkInputValidity = (elem) => {
    const { isValid, isEmpty, isPatternMismatch, isTooShort, isTooLong, hasConfirm, confirmTarget, isNotEqual, isCustomError } = this.getValidity(elem);
    const type = elem.getAttribute('type');
    const starsRatingElem = type === 'radio' ? elem.closest('.stars-rating') : null;
    const invalidFeedback = starsRatingElem ?
      starsRatingElem.parentElement.querySelector('.invalid-feedback') :
      elem.parentElement.querySelector('.invalid-feedback');


    elem.classList.toggle('invalid', !isValid);
    if (starsRatingElem) {
      starsRatingElem.classList.toggle('invalid', !isValid);
      if (invalidFeedback && isEmpty) invalidFeedback.innerHTML = starsRatingElem.dataset['emptyRating'] || this.DICTIONARY['emptyRating'];
    }
    else if (isEmpty && invalidFeedback) {
      if (type === 'file' && elem.closest('.file-control.validated')) { /* empty */ }
      else invalidFeedback.innerHTML = elem.dataset['emptyText'] || this.DICTIONARY['empty'];
    }
    else if (isTooShort && invalidFeedback) invalidFeedback.innerHTML = elem.dataset['tooShort'] || this.DICTIONARY['tooShort'] + elem.minLength;
    else if (isTooLong && invalidFeedback) invalidFeedback.innerHTML = elem.dataset['tooLong'] || this.DICTIONARY['tooLong'] + elem.maxLength;
    else if (isPatternMismatch && invalidFeedback) invalidFeedback.innerHTML = elem.dataset['patternMismatch'] || this.DICTIONARY[type] || this.DICTIONARY['patternMismatch'];
    else if (isCustomError && invalidFeedback) invalidFeedback.innerHTML = elem.validationMessage || elem.dataset['customError'] || this.DICTIONARY['emptyText'] || this.DICTIONARY['empty'];

    if (hasConfirm && confirmTarget) {
      confirmTarget.classList.toggle('invalid', isNotEqual);
      if (isNotEqual && invalidFeedback) invalidFeedback.innerHTML = elem.dataset['notEqual'] || this.DICTIONARY['notEqual'];
    }

    return isValid;
  };

  /***
   * Обновление валидации формы и изменяемого input
   * @param target {HTMLInputElement} - изменяемый input
   */
  checkFormValidity = ({ target }) => {
    const isValid = this.getFormValidity();
    this.checkInputValidity(target);

    this.block.classList.toggle('invalid', !isValid);
    this.block.classList.toggle('valid', isValid);
    this.toggleSubmitButton(!isValid);
  };

  checkImportantValidity = () => {
    const isValid = every(this.importantElements, (elem) => {
      if (elem.disabled || elem.readOnly) return true;
      else return get(this.getValidity(elem), 'isValid');
    });
    this.toggleSubmitButton(!isValid);
  };

  /***
   * Метод получение валидности элемента
   * @param elem {HTMLInputElement} - инпут
   * @return Validity
   */
  getValidity = (elem) => {
    /**
     * @typedef {Object} Validity
     * @property {boolean} isValid - совокупность всех проверок
     * @property {boolean} isPatternMismatch - не соответствие паттерну
     * @property {boolean} isTooShort - слишком короткий текст
     * @property {boolean} isTooLong - слишком длинный текст
     * @property {boolean} isEmpty - пустой
     * @property {boolean} hasConfirm - есть связанный инпут
     * @property {HTMLInputElement|null} confirmTarget - связанный инпут
     * @property {boolean} isNotEqual - связанные инпуты не равны
     */
    const isEmpty = elem.validity.valueMissing;
    const isPatternMismatch = elem.validity.patternMismatch;
    const isTooShort = elem.validity.tooShort;
    const isTooLong = elem.validity.tooLong;
    const isCustomError = elem.validity.customError;
    const hasConfirm = elem.hasAttribute('data-confirm');

    const Validity = {
      isValid: !(isEmpty || isPatternMismatch || isTooShort || isTooLong || isCustomError),
      isEmpty, isPatternMismatch, isTooShort, isTooLong, hasConfirm, isCustomError,
      confirmTarget: null,
      isNotEqual: false,
    };

    if (hasConfirm) {
      const targetSelector = elem.dataset['confirmTarget'];
      if (!targetSelector) return this.BlockError.error('Отсутствует data-confirm-target у data-confirm');

      const confirmTarget = this.block.querySelector(targetSelector);
      const isNotEqual = confirmTarget.value !== elem.value;
      const isValid = !(isEmpty || isPatternMismatch || isTooShort || isTooLong || isNotEqual || isCustomError);
      merge(Validity, { isValid, confirmTarget, isNotEqual });
    }

    return Validity;
  };

  /***
   * toggle кнопок отправки
   * @param isInvalidForm {boolean} - вкл/выкл
   */
  toggleSubmitButton = (isInvalidForm) => {
    forEach(this.submitButtons, (button) => button.disabled = isInvalidForm);
  };

  /***
   * Отобразить invalid класс и отменить отправку
   * @param evt {Event} - submit event
   */
  showInvalid = (evt) => {
    evt && evt.preventDefault();
    this.block.classList.add('was-validated');
  };

  /***
   * Получение валидности формы. Есть элементы, которые не учитывает стандартный метод валидации
   * @return {boolean} - true - валидная, false - не валидная
   */
  getFormValidity = () => every(this.elements, (elem) => {
    if (elem.disabled || elem.readOnly) return true;
    else return get(this.getValidity(elem), 'isValid');
  });

  /***
   * Сбросить валидацию формы
   */
  resetValidity = () => {
    this.block.classList.remove('invalid');
    this.block.classList.remove('valid');
    this.block.classList.remove('was-validated');
    this.isValidForm = true;
    forEach(this.elements, (elem) => { elem.classList.remove('invalid'); });
    this.toggleSubmitButton(false);
    if (this.importantElements.length) {
      this.checkImportantValidity();

      // Удаление слушателей если они ещё есть
      this.block.removeEventListener('input', this.checkImportantValidity);
      this.block.removeEventListener('change', this.checkImportantValidity);

      // Установка новых
      this.block.addEventListener('input', this.checkImportantValidity);
      this.block.addEventListener('change', this.checkImportantValidity);
    }

    this.block.removeEventListener('input', this.checkFormValidity);
    this.block.removeEventListener('change', this.checkFormValidity);
  };

  destroy = () => {
    if (this.block) {
      this.block.removeEventListener('submit', this.checkValidity);
      this.block.removeEventListener('input', this.checkFormValidity);
      this.block.removeEventListener('change', this.checkFormValidity);
      this.block.removeEventListener('input', this.checkImportantValidity);
      this.block.removeEventListener('change', this.checkImportantValidity);
      this.block.removeEventListener('reset', this.resetValidity);
      this.block.classList.remove('validation_inited');
      delete this.block.Validation;
    }
  };

  static init() {
    Array.from(document.querySelectorAll('form[data-iv-validation]:not(.validation_inited)')).forEach((elem) => new Validation(elem));
  }
}

document.addEventListener('DOMContentLoaded', Validation.init);

// Экспорт;
window.Block.Validation = Validation;
